import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { ConfigService } from '@sp-client/app/services/config.service';
import { LoginService } from '@sp-client/app/services/login.service';
import { PlanService } from '@sp-client/app/services/plan.service';
import { filter, map, take, tap, combineLatest, timeout, catchError, of, switchMap } from 'rxjs';
import { Routes } from '../enums/routes.enum';
import { LoadingService } from '../services/loading.service';
import { LogService } from '../services/log.service';
import { cm } from '@sp-shared';

export const authGuard: CanActivateFn = (_route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
	const configService = inject(ConfigService);
	const log = inject(LogService);

	if (!configService.isBrowserSignal()) {
		return true;
	}

	const loginService = inject(LoginService);
	const planService = inject(PlanService);
	const router = inject(Router);
	const loading = inject(LoadingService);

	loading.start();

	const targetUrl = state.url;

	// Allow core auth routes without checks
	const publicAuthRoutes: string[] = [
		Routes.Login,
		Routes.SignUp,
		Routes.SignUpPassword,
		Routes.SignUpEmail,
		Routes.ForgotPassword,
		Routes.PasswordReset,
		Routes.Logout
	];
	const isOnboardFlow = [
		Routes.Onboard,
		Routes.OnboardCountry,
		Routes.OnboardRegion,
		Routes.OnboardDemographic,
		Routes.OnboardReview
	].some(r => targetUrl.startsWith(r));

	if (publicAuthRoutes.some(r => targetUrl.startsWith(r))) {
		loading.stop();
		return true;
	}

	// Convert signals to observables in injection context
	const isLoading$ = toObservable(planService.isLoadingSignal);
	const plans$ = toObservable(planService.plansSignal);
	const selectedPlan$ = toObservable(planService.selectedPlanSignal);
	const failedToFetch$ = toObservable(planService.failedToFetch);
	const failedToken$ = toObservable(loginService.failedTokenSignal);

	// First check authentication
	return toObservable(loginService.loggedInSignal).pipe(
		tap(val => log.debug('AuthGuard signal:', val)),
		filter((val): val is boolean => val !== null),
		take(1),
		switchMap(isLoggedIn => {
			log.debug('AuthGuard: isLoggedIn', isLoggedIn);
			if (isLoggedIn === null) {
				loading.stop();
				throw new Error('Map should never receive null');
			}

			if (!isLoggedIn) {
				if (isOnboardFlow) {
					log.debug('AuthGuard: not logged in on onboard route, normalizing to /onboard/country');
					loading.stop();
					return of(router.createUrlTree([Routes.OnboardCountry]));
				}
				log.debug('AuthGuard: not logged in, redirecting to /login');
				loading.stop();
				return of(router.createUrlTree([Routes.Login]));
			}

			// User is logged in, now check for valid plan
			log.debug('AuthGuard: logged in, checking plan validity');

			// Skip plan check for onboard routes
			if (isOnboardFlow) {
				log.debug('AuthGuard: onboard route, skipping plan validation');
				loading.stop();
				return of(true);
			}

			// Wait for plans to load and check validity
			return combineLatest([isLoading$, plans$, selectedPlan$, failedToFetch$, failedToken$]).pipe(
				// Wait until we either have plan data, or we know plan/token resolution failed.
				filter(([isLoading, plans, selectedPlan, failedToFetch, failedToken]) => {
					if (failedToken || failedToFetch) {
						return true;
					}
					const hasPlanData = cm.isNotEmpty(plans) || cm.isNotEmpty(selectedPlan);
					return !isLoading && hasPlanData;
				}),
				take(1),
				// Add timeout to prevent indefinite waiting
				timeout(10000), // 10 second timeout
				map(([_, plans, selectedPlan, failedToFetch, failedToken]) => {
					log.debug('AuthGuard: Plan resolution complete, checking validity', {
						plans,
						selectedPlan,
						failedToFetch,
						failedToken
					});

					if (failedToken || failedToFetch) {
						log.debug('AuthGuard: Plan/token resolution failed, redirecting to /login');
						loading.stop();
						return router.createUrlTree([Routes.Login]);
					}

					// Check if user has any plans
					if (cm.isEmpty(plans) && cm.isEmpty(selectedPlan)) {
						log.debug('AuthGuard: No plans found, redirecting to /login');
						loading.stop();
						return router.createUrlTree([Routes.Login]);
					}

					// Check selected plan first (most common case)
					if (cm.isNotEmpty(selectedPlan)) {
						if (selectedPlan.purchaseEmailSent === true) {
							log.debug('AuthGuard: Selected plan has purchaseEmailSent = true, allowing access');
							loading.stop();
							return true;
						}
						log.debug('AuthGuard: Selected plan has purchaseEmailSent = false', selectedPlan);
					}

					// Check all plans to see if any have purchaseEmailSent = true
					if (cm.isNotEmpty(plans)) {
						const hasValidPlan = plans.some(plan => plan.purchaseEmailSent === true);
						if (hasValidPlan) {
							log.debug('AuthGuard: Found valid plan with purchaseEmailSent = true, allowing access');
							loading.stop();
							return true;
						}
						log.debug('AuthGuard: No plans with purchaseEmailSent = true found, redirecting to /login');
					}

					// No valid plan found
					log.debug('AuthGuard: No valid plan found, redirecting to /login');
					loading.stop();
					return router.createUrlTree([Routes.Login]);
				}),
				catchError(err => {
					log.error('AuthGuard: Error checking plan validity', err);
					loading.stop();
					// On timeout or error, redirect to login for safety
					return of(router.createUrlTree([Routes.Login]));
				})
			);
		})
	);
};
